<script setup lang="ts">
import useBreakpointsLimit from "~/composables/use-breakpoints-limit";
import useList from "~/composables/use-list";
import usePagination from "~/composables/use-pagination";
import { EVENT_STATUS, EVENT_TYPE } from "~/constants";
import EventsAPI from "~/services/events";
import { returnPaginatedResponse } from "~/services/format";
import RecommendationsAPI from "~/services/recommendations";
import SearchAPI from "~/services/search";
import useEventStore from "~/stores/event";
import useUserStore from "~/stores/user";
import getErrorLocalePathMessage from "~/utils/locale";

export interface ListEventsProps {
  /**
   * An optional string that will be used as the title of the list.
   */
  title?: string;
  /**
   * An optional string that represents the id of an event.
   */
  eventId?: string;
  /**
   * A string that indicates the type of events to display.
   */
  type?: "archived" | "highlight" | "upcoming" | "live";
  /**
   * An optional number that specifies the maximum number of events to display.
   */
  limit?: number;
  /*
  * An optional list off the items
  */
  items?: IModelEvent[] | null;
  /**
   * An optional string that represents the subcategory id of events to display.
   */
  subCategoryId?: string;
  /**
   * An optional string that represents the main category id of events to display.
   */
  mainCategoryId?: string;
  /**
   * An optional string that represents the team id of events to display.
   */
  teamId?: string;
  /**
   * An optional string that will be used as the root class of the component.
   */
  rootClass?: string;
  /**
   * A boolean that indicates whether to show the suggested events. `You may also like` section
   */
  suggested?: boolean;
  /**
   * A boolean that indicates whether to show the recommended events. `Following..` section. This section shows events based on the user category following.
   */
  recommended?: boolean;
  /**
   * A boolean that indicates whether to load the events when the component becomes visible.
   */
  loadWhenVisible?: boolean;
  /**
   * A boolean that indicates whether to hide the component when there is no content to display.
   */
  hideOnNoContent?: boolean;
  /**
   * A optional string that indicates no content text
   */
  noContentText?: string;
  /**
   * A boolean that indicates whether to show a "See All" link.
   */
  hasSeeAllLink?: boolean;
  /**
   * A string that indicates the placement of the "See All" link.
   */
  seeAllLinkPlacement?: "bottom-center" | "top-right";
  /**
   * A boolean that indicates whether to show a "Load More" button.
   */
  hasLoadMoreButton?: boolean;
  /**
   * An optional date range object that represents the range of dates for the events to display.
   */
  dateRange?: TDateRange;
  /**
   * An optional array of sport types to filter the events by.
   */
  sportTypeFilter?: TSportType[] | TSportType;
  /**
   * An optional string to search the events by.
   */
  searchValue?: string;
  /**
   * An optional gender to filter the events by.
   */
  gender?: TGender | "all";
  /**
   * An optional string that represents the key to group the events by.
   */
  groupByKey?: keyof IModelEvent;
  /**
   * A boolean that indicates whether to group the events by date.
   */
  groupByDate?: boolean;
  /**
   * A boolean that indicates whether to exclude an event with the given `eventId` from the list.
   */
  excludeWithEventId?: boolean;
  advancedSearch?: boolean;
  loading?: boolean;
}

const { t, te } = useI18n();
const userStore = useUserStore();
const eventStore = useEventStore();
const useLimit = useBreakpointsLimit();
const emit = defineEmits(["noItems", "hasItems"]);
const props = withDefaults(defineProps<ListEventsProps>(), {
  type: "archived",
  seeAllLinkPlacement: "bottom-center",
});

const staticLimit = 20;
const dynamicLimit = useLimit.value;

const limit = props.limit ? props.limit : staticLimit;

const seeAllPath = computed(() => {
  let path = "";
  switch (true) {
    case props.suggested:
      path = "/suggested/";
      break;
    case props.recommended:
      path = "/recommendation/";
      break;
    case props.type === "archived":
      path = "/latest/";
      break;
    default:
      path = `/${props.type}/`;
      break;
  }
  return path;
});

const hasSearch = computed(() => {
  return Boolean(props.advancedSearch && (props.searchValue
  || props.sportTypeFilter?.length
  || (props.dateRange && props.dateRange !== "anytime")
  || (props.gender && props.gender !== "all")));
});

const { items: events, loadMore, error, loading, canLoadMore, reset } = usePagination<IModelEvent>({
  immediate: false,
  pageSize: limit,
  paginateExistItems: Boolean(props.suggested && props.eventId),
  loadItemsFn: (page: number, pageSize: number) => {
    if (props.items) {
      return Promise.resolve(returnPaginatedResponse([]));
    }
    const params: Record<string, string | undefined | TSportType[]> = {};

    params.type = props.type;

    if (hasSearch.value) {
      return SearchAPI.eventSearch({
        searchString: props.searchValue,
        type: props.type,
        size: limit,
        gender: props.gender,
        page,
        sportType: props.sportTypeFilter,
        dateRange: props.dateRange,
        identityId: props.mainCategoryId,
      });
    }

    if (props.type === EVENT_TYPE.HIGHLIGHT) {
      const params: IRequestGameHighlightsOptions = { size: limit, page };
      if (props.subCategoryId)
        params.eventFilters = { "identities.id": props.subCategoryId };
      if (props.teamId)
        params.eventFilters = { "search.teams.id": props.teamId };

      return EventsAPI.userMedia.loadHighlights(params);
    }

    if (props.recommended)
      return RecommendationsAPI.load({ size: limit, page, next: true });

    if (props.suggested)
      params.subCategoryId = Object.keys(userStore.followed).join("|");
    else
      params.subCategoryId = props.subCategoryId;

    if (props.suggested && props.eventId)
      return EventsAPI.loadRelatedVideos({ id: props.eventId, excludeVideo: Boolean(props.excludeWithEventId) });

    return EventsAPI.load({
      page,
      pageSize,
      ...params,
      sportTypeFilter: props.sportTypeFilter,
      mainCategoryId: props.mainCategoryId,
      teamId: props.teamId,
    });
  },
});

const items = computed(() => {
  return props.items ? props.items : events.value;
});

const errorMessage = computed(() => error.value && getErrorLocalePathMessage(error.value, { t, te }).message);
const canShowSeeAllLink = computed(() => props.hasSeeAllLink && (canLoadMore.value || (items.value.length > dynamicLimit)));
const seeAllLinkPath = computed(() => ({
  path: seeAllPath.value,
  query: {
    teamId: props.teamId,
    subCategoryId: props.subCategoryId,
    mainCategoryId: props.mainCategoryId && props.mainCategoryId !== "all" ? props.mainCategoryId : undefined,
    eventId: props.eventId,
    q: props.searchValue ? props.searchValue : undefined,
    sport_type: props.sportTypeFilter,
    event_date: props.dateRange === "anytime" ? undefined : props.dateRange,
    gender: props.gender === "all" ? undefined : props.gender,
    search: props.advancedSearch ? "true" : undefined,
  },
}));

const filteredItems = useList(items, {
  limit: props.hasLoadMoreButton ? undefined : props.limit || dynamicLimit,
});

watch(
  () => [props.subCategoryId, props.mainCategoryId],
  () => {
    if (!props.items) {
      reset();
    }
  },
  { immediate: !props.loadWhenVisible },
);
// TODO: Improve getting list of suggested events after buying some commerce.
watch(() => eventStore.canReloadSuggested, (canReload) => {
  if (canReload)
    reset();
});

watch(() => items.value, () => {
  if (items.value.length === 0)
    emit("noItems");
  else
    emit("hasItems");
});
</script>

<template>
  <BaseList
    :title="props.title"
    :items="filteredItems"
    :loading="loading || props.loading"
    :error="errorMessage"
    :limit="limit"
    :no-content-text="props.noContentText"
    :root-class="props.rootClass"
    :hide-on-no-content="props.hideOnNoContent"
    :load-when-visible="props.loadWhenVisible"
    :see-all-link-path="seeAllLinkPath"
    :see-all-link-placement="props.seeAllLinkPlacement"
    :has-see-all-link="canShowSeeAllLink"
    :has-load-more-button="props.hasLoadMoreButton && canLoadMore"
    :group-by-key="props.groupByKey"
    :group-by-date="props.groupByDate"
    @click-load-more="loadMore()"
    @visible="reset()"
  >
    <template #start>
      <slot name="start" />
    </template>
    <template #item="{ item }">
      <BaseButton
        v-if="props.type === EVENT_STATUS.LIVE"
        tag="nuxt-link"
        :name="`item-live-${item.id}`"
        type="icon"
        :to="{ path: `/events/${item.id}/` }"
        class="card-item-link"
      >
        <CardLive
          v-bind="item"
          :title="item.metaTitle"
          :statistic-tooltip-title="t('labels.statistics')"
          :sport-type-tooltip-title="item.sportType ? t(`sport_types.${item.sportType}`) : ''"
        />
      </BaseButton>
      <BaseButton
        v-else-if="props.type === EVENT_STATUS.UPCOMING"
        tag="nuxt-link"
        :name="`item-upcoming-${item.id}`"
        type="icon"
        :to="{ path: `/events/${item.id}/` }"
        class="card-item-link"
      >
        <CardUpcoming
          v-bind="item"
          :title="item.metaTitle"
          :sport-type-tooltip-title="item.sportType ? t(`sport_types.${item.sportType}`) : ''"
        />
      </BaseButton>
      <UserCardEvent v-else :item="item" has-bookmark />
    </template>
    <template #skeleton>
      <CardLiveSkeleton v-if="props.type === EVENT_STATUS.LIVE" />
      <CardUpcomingSkeleton v-else-if="props.type === EVENT_STATUS.UPCOMING" />
      <CardEventSkeleton v-else />
    </template>
    <template #empty>
      <slot name="empty">
        <div v-if="props.loadWhenVisible" class="min-h-800px" />
        <BaseNotFound v-else-if="!props.hideOnNoContent" :text="t('labels.empty_events_list')">
          <template #icon="{ isDarkTheme }">
            <IconEventNotfound :is-dark-theme="isDarkTheme" />
          </template>
        </BaseNotFound>
      </slot>
    </template>
    <template #end>
      <slot name="end" />
    </template>
  </BaseList>
</template>
